import { ClassifierToolTypes, RegressionToolTypes, TextClassifierToolTypes, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { MetadataMap } from '../metadataMap';
import { getRenamedColumns } from '../toolConfigUtil';
import { SourceValidationResult } from './sourceValidationResult';
import { ToolValidationResult, ToolValidationState } from './toolValidationResult';
export const validateSourceWithDetail = (source, inputs, outputs) => {
    // Checks source column types against valid types provided by inputs. Also checks for name conflicts
    // between inputs and newly added columns provided by outputs.
    const inputNames = new Set();
    const outputNames = new Set(outputs);
    for (const column of source) {
        // Check for the same name in source and outputs.
        // NOTE: This check isn't appropriate for all tools. A column could be in source, be removed by the tool,
        // and therefore adding a new column with that name is ok. Aggregrate is one example.
        if (outputNames.has(column.name)) {
            return new SourceValidationResult(false, `Output column name conflict: ${column.name}.`, `Please rename the alias in the configuration or the columns in the source: ${column.name}.`);
        }
        if (!inputNames.has(column.name) && inputs.has(column.name)) {
            if (inputs instanceof Map) {
                const types = new Set(inputs.get(column.name));
                if (types.size > 0 && !types.has(column.type)) {
                    return new SourceValidationResult(false, `Column type mismatch: ${column.name}. Expected: ${Array.from(types).join(', ')}, Actual: ${column.type}.`, `Please change the column type or select a column with the type of ${Array.from(types).join(', ')}.`);
                }
            }
            inputNames.add(column.name);
        }
    }
    if (inputNames.size !== inputs.size) {
        const sourceColumns = Array.from(source).map((col) => col.name);
        if (inputs instanceof Set) {
            const missingColumns = Array.from(inputs).filter((name) => !inputNames.has(name));
            return new SourceValidationResult(false, `Missing columns in source: ${missingColumns.join(', ')}.`, `**join** the nodes to get all the columns OR select columns form the available columns: ${sourceColumns.join(', ')}.`);
        }
        else if (inputs instanceof Map) {
            const missingColumns = Array.from(inputs)
                .filter(([name]) => !inputNames.has(name))
                .map(([name]) => name);
            return new SourceValidationResult(false, `Missing columns in source: ${missingColumns.join(', ')}.`, `**join** the nodes to get all the columns OR select columns form the available columns: ${sourceColumns.join(', ')}.`);
        }
        else {
            return new SourceValidationResult(false);
        }
    }
    return new SourceValidationResult(true);
};
export const isSourceValid = (source, inputs, outputs) => {
    return validateSourceWithDetail(source, inputs, outputs).isValid;
};
export const buildInputsMap = (columns, types) => {
    return columns.reduce((map, c) => map.set(c, types), new Map());
};
export const isTextClassifierValidForCalc = (port, scope, textColumn, labelColumn) => {
    const source = scope.schemas.at(port);
    if (!source) {
        return ToolValidationResult.getMissingSourceColumnsResult();
    }
    const columnNames = source.map((col) => col.name);
    const predictionColumnName = getRenamedColumns(columnNames, 'Prediction', ToolType.TEXT_CLASSIFIER);
    const probabilityColumnName = getRenamedColumns(columnNames, 'Probability', ToolType.TEXT_CLASSIFIER);
    const inputs = new Map([
        [textColumn, ['string']],
        [labelColumn, ['string', 'boolean']]
    ]);
    const outputs = [predictionColumnName, probabilityColumnName];
    const sourceValidationResult = validateSourceWithDetail(source, inputs, outputs);
    return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
};
export const isTextClassifierValidForRun = (port, scope, textColumn, pass) => {
    if (pass) {
        return scope.schemas.has(port) ? ToolValidationState.VALID : ToolValidationState.BAD_METADATA;
    }
    else {
        const source = scope.schemas.at(port);
        if (!source) {
            return ToolValidationState.BAD_METADATA;
        }
        const columnNames = source.map((col) => col.name);
        const predictionColumnName = getRenamedColumns(columnNames, 'Prediction', ToolType.TEXT_CLASSIFIER);
        const probabilityColumnName = getRenamedColumns(columnNames, 'Probability', ToolType.TEXT_CLASSIFIER);
        const outputs = [predictionColumnName, probabilityColumnName];
        const inputs = new Map([[textColumn, ['string', 'boolean']]]);
        return isSourceValid(source, inputs, outputs) ? ToolValidationState.VALID : ToolValidationState.BAD_METADATA;
    }
};
// LabelType cannot be saved as part of the ML tool, as you can change an upstream
// tool (like changeType) that will alter the label type after the ML tool is already saved
// Here, we calculate what the original label type is based on the ORIGINAL data sources
// https://varicent.atlassian.net/browse/WP-4829
export const TrainingTools = new Set([
    ...ClassifierToolTypes,
    ...RegressionToolTypes,
    ...TextClassifierToolTypes,
    ToolType.PREDICTOR
]);
export const updateLabelTypesForMLToolsForDependeeMap = (iConfigHelper, dataSources, dependees) => {
    dependees.forEach((graph, key) => {
        updateLabelTypesForMLToolsForDependee(iConfigHelper, dataSources, key, dependees);
    });
};
export const updateLabelTypesForMLToolsForDependee = (iConfigHelper, dataSources, dependeePipeID, dependeeMap) => {
    const calculatedElements = Array.from(dependeeMap.get(dependeePipeID).elements);
    updateLabelTypesForMLTools(iConfigHelper, dependeePipeID, dataSources, calculatedElements, dependeeMap);
};
export const updateLabelTypesForMLTools = (iConfigHelper, pipeID, dataSources, elements, dependees, passIDs = new Set()) => {
    const metaMap = new MetadataMap({
        pipeID,
        data: dataSources,
        elements,
        dependees,
        forCalc: false,
        iConfigHelper,
        ignoreSamples: true,
        passIDs
    });
    elements.forEach((ele) => {
        if (ele.elementType === 'node') {
            const nodeEle = ele;
            if (TrainingTools.has(nodeEle.toolType)) {
                // found a ML tool, get its output and find the label column
                const config = nodeEle.config;
                if (config) {
                    const outputCols = metaMap.getOutput(nodeEle.elementID).schemas.at(config.out);
                    if (outputCols) {
                        config.labelColumnType = outputCols.find((colMet) => colMet.name === config.labelColumn)?.type;
                    }
                }
            }
        }
    });
};
//# sourceMappingURL=validateHelper.js.map